import { useEffect, useId, useRef } from 'react';

import { createPortal } from 'react-dom';

import { AnimatePresence } from 'motion/react';
import PropTypes from 'prop-types';

import OverlayFadeMotion from 'components/Motion/OverlayFadeMotion';
import SliderBottomMotion from 'components/Motion/SliderBottomMotion';

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export const BottomSheet = ({
  open,
  onClose = () => {},
  onExited,
  disableClose = false,
  allowDismiss = false,
  title,
  description,
  children,
  footer,
  dataTestId,
}) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const previousActiveElement = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleDragEnd = (event, info) => {
    if (info.offset.y > 100 || info.velocity.y > 500) {
      onCloseRef.current();
    }
  };

  // Lock background scroll, trap Tab focus, and handle Escape — mirrors Drawer.jsx.
  useEffect(() => {
    if (open) {
      previousActiveElement.current = document.activeElement;
      const prevHtml = document.documentElement.style.overflow;
      const prevBody = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      const handleKeyDown = e => {
        if (e.key === 'Escape') {
          if (!disableClose) onCloseRef.current();
          return;
        }
        if (
          e.key !== 'Tab' ||
          !panelRef.current ||
          !panelRef.current.contains(document.activeElement)
        )
          return;
        const focusable = panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.documentElement.style.overflow = prevHtml;
        document.body.style.overflow = prevBody;
        previousActiveElement.current?.focus?.();
      };
    }
  }, [open, disableClose]);

  // Move focus into the sheet when it opens
  useEffect(() => {
    if (open && panelRef.current) {
      const focusable = panelRef.current.querySelector(FOCUSABLE_SELECTOR);
      (focusable || panelRef.current).focus({ preventScroll: true });
    }
  }, [open]);

  const sheetContent = (
    <AnimatePresence onExitComplete={onExited}>
      {open && (
        <>
          <OverlayFadeMotion
            keyProp="bottom-sheet-backdrop"
            className="fixed inset-0 bg-black/30 z-[9998]"
            duration={0.2}
            onClick={() => {
              if (!disableClose) onCloseRef.current();
            }}
          />

          <SliderBottomMotion
            keyProp="bottom-sheet-panel"
            ref={panelRef}
            className="fixed bottom-0 left-0 right-0 z-[9999] flex max-h-[85vh] w-full flex-col rounded-t-[20px] bg-white shadow-[0px_-8px_24px_0px_rgba(0,0,0,0.25)] outline-none"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            tabIndex={-1}
            data-testid={dataTestId}
            {...(allowDismiss && {
              drag: 'y',
              dragConstraints: { top: 0, bottom: 0 },
              dragElastic: { top: 0, bottom: 1 },
              onDragEnd: handleDragEnd,
            })}>
            {allowDismiss && (
              <div className="flex w-full shrink-0 items-center justify-center pb-[6px] pt-[10px]">
                <div className="h-[4px] w-[36px] rounded-full bg-neutral-4" />
              </div>
            )}

            {(title || description) && (
              <div
                className={`flex shrink-0 flex-col gap-2 px-8 pb-4 ${allowDismiss ? '' : 'pt-8'}`}>
                {title && (
                  <p
                    id={titleId}
                    className="text-neutral-7 font-medium text-4xl capitalize display-font">
                    {title}
                  </p>
                )}
                {description && (
                  <p className="m-0 text-xl font-light text-text-tertiary">{description}</p>
                )}
              </div>
            )}
            {(title || description) && <div className="h-px w-full shrink-0 bg-neutral-4" />}

            <div className="min-h-0 flex-1 overflow-y-auto p-8">{children}</div>

            {footer && (
              <>
                <div className="h-px w-full shrink-0 bg-neutral-4" />
                <div className="flex shrink-0 flex-col items-center gap-3 px-8 pt-8 pb-[max(32px,env(safe-area-inset-bottom))]">
                  {footer}
                </div>
              </>
            )}
          </SliderBottomMotion>
        </>
      )}
    </AnimatePresence>
  );

  return typeof document !== 'undefined' ? createPortal(sheetContent, document.body) : null;
};

BottomSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onExited: PropTypes.func,
  disableClose: PropTypes.bool,
  allowDismiss: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  dataTestId: PropTypes.string,
};
